"use client";

import { FC, ReactNode } from "react";
import FormMaster from "@/components/uix/forms/MasterForm/FormMaster";
import { useAppDispatch, useAppSelector } from "@/store/hook";
import { FaCheckCircle } from "react-icons/fa";
import CustomToast, { CustomToastProps } from "@/components/uix/CustomToast/CustomToast";
import { TypeErrorPrisma } from "@/types/prisma.types";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TypeContactCreate } from "@/types/system/contact.types";
import { selectCar, selectCarLoadingStates } from "@/store/selectors/data/CarSelectors";
import {
  selectOffersSelected,
  selectOffersSelectedLoadingStates,
} from "@/store/selectors/jsonld/OfferSelector";
import { createContactThunk } from "@/store/thunks/data/ContactThunks";
import { SchemaContactCreate } from "@/schemas/system/Contact.schema";
import { BiSolidCommentError } from "react-icons/bi";
import { loadOffersFromLocalStorage, removeOffersFromLocalStorage } from "@/storages/OffersStorage";
import { useRouter } from "next/navigation";

interface TContainerFormProps {
  children: ReactNode;
}

const defaultValues: TypeContactCreate = {
  firstName: "",
  lastName: "",
  email: "",
  message: "",
  indicatif: "+596",
  telephone: "",
  offersIds: [],
};

const customToast: CustomToastProps = {
  type: "success",
  titleText: "Success !!",
  descriptionText: "Merci votre message à bien été envoyer !",
  duration: 3000,
  icon: <FaCheckCircle />,
};

const customErrorToast: CustomToastProps = {
  type: "error",
  titleText: "Error !!",
  descriptionText: "Désolé votre message n'à pas été bien envoyer !",
  duration: 3000,
  icon: <BiSolidCommentError />,
};

const ContactForm: FC<TContainerFormProps> = ({ children }: TContainerFormProps) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const car = useAppSelector(selectCar);
  const carLoading = useAppSelector(selectCarLoadingStates);
  const offers = useAppSelector(selectOffersSelected);
  const offerLoading = useAppSelector(selectOffersSelectedLoadingStates);

  const methods = useForm<TypeContactCreate>({
    defaultValues,
    resolver: zodResolver(SchemaContactCreate),
    mode: "onChange",
  });

  const { setError, reset } = methods;

  const handleNotify = async (data: TypeContactCreate) => {
    const offersStorage = loadOffersFromLocalStorage();

    // Ajouter les valeurs supplémentaires avant la soumission
    const extendedData = {
      ...data,
      ...(car && carLoading ? { engineId: car.id } : {}),
      ...(offers && offerLoading ? { offersIds: offersStorage?.offersIds } : {}),
    };

    const resultAction = await dispatch(createContactThunk(extendedData));

    if (createContactThunk.rejected.match(resultAction)) {
      const errorMessage = resultAction.payload as TypeErrorPrisma;
      if (errorMessage?.errors) {
        errorMessage.errors.forEach((err: { path: keyof TypeContactCreate; message: any }) => {
          setError(err.path, { type: "server", message: err.message });
        });
      }
      CustomToast(customErrorToast);
    } else if (createContactThunk.fulfilled.match(resultAction)) {
      CustomToast(customToast);
      removeOffersFromLocalStorage();
      reset();
      router.push("/", { scroll: true });
    }
  };

  const handleSubmit = async (data: TypeContactCreate) => {
    await handleNotify(data);
  };

  return (
    <FormMaster
      textButton='Envoyer'
      methods={methods}
      schema={SchemaContactCreate}
      onSubmit={handleSubmit}
    >
      {children}
    </FormMaster>
  );
};

export default ContactForm;
